import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
	ClockIcon,
	ExclamationTriangleIcon,
	InformationCircleIcon,
	LockClosedIcon,
	PencilIcon,
	TrashIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import AppLayout from '../../components/layout/AppLayout';
import { Button, ConfirmDialog, Modal, Spinner } from '../../components/ui';
import { businessHoursApi, timeOffApi, ApiError } from '../../api';
import type { LocationClosure, TimeOffSaveAffectedBooking } from '../../types/timeOff';
import useProviderProfileStore from '../../stores/providerProfileStore';
import { DAYS_OF_WEEK } from '../../types/businessHours';
import type { AffectedBooking, BusinessHoursDay, DayOfWeek } from '../../types/businessHours';

// ─── Constants & helpers ────────────────────────────────────────

const DAY_LABELS: Record<DayOfWeek, string> = {
	MONDAY: 'Monday',
	TUESDAY: 'Tuesday',
	WEDNESDAY: 'Wednesday',
	THURSDAY: 'Thursday',
	FRIDAY: 'Friday',
	SATURDAY: 'Saturday',
	SUNDAY: 'Sunday',
};

const FALLBACK_OPEN = '09:00';
const FALLBACK_CLOSE = '18:00';

/** `<input type="time">` only accepts HH:mm — the API may send HH:mm:ss or an empty string. */
const toHHmm = (value?: string | null): string => {
	if (!value) return '';
	const match = /^(\d{1,2}):(\d{2})/.exec(value.trim());
	if (!match) return '';
	return `${match[1].padStart(2, '0')}:${match[2]}`;
};

/** Either response shape the business-hours endpoints can send back. */
type WeekSource = BusinessHoursDay[] | { days?: BusinessHoursDay[] | null } | null | undefined;

/**
 * The grid always shows a full Mon–Sun week, so missing days fall back to a
 * standard open day. The GET sends a bare array and the PUT wraps the week in
 * a days key; both are accepted so a change to either verb cannot break the page.
 */
const normalizeWeek = (source: WeekSource): BusinessHoursDay[] => {
	const week = Array.isArray(source) ? source : Array.isArray(source?.days) ? source.days : [];
	return DAYS_OF_WEEK.map(dayOfWeek => {
		const match = week.find(d => d.dayOfWeek?.toUpperCase() === dayOfWeek);
		return {
			dayOfWeek,
			closed: match?.closed ?? false,
			open24: match?.open24 ?? false,
			openTime: toHHmm(match?.openTime) || FALLBACK_OPEN,
			closeTime: toHHmm(match?.closeTime) || FALLBACK_CLOSE,
		};
	});
};

/** A day that closes earlier than it opens runs past midnight into the next day. */
const isOvernight = (day: BusinessHoursDay): boolean =>
	!day.closed && !day.open24 && Boolean(day.openTime) && day.closeTime < day.openTime;

const isAlwaysOpen = (days: BusinessHoursDay[]): boolean =>
	days.length > 0 && days.every(d => !d.closed && d.open24);

// ─── Toggle switch ──────────────────────────────────────────────

const ToggleSwitch = ({
	checked,
	onChange,
	disabled,
	ariaLabel,
}: {
	checked: boolean;
	onChange: (checked: boolean) => void;
	disabled?: boolean;
	ariaLabel: string;
}) => (
	<button
		type="button"
		role="switch"
		aria-checked={checked}
		aria-label={ariaLabel}
		disabled={disabled}
		onClick={() => onChange(!checked)}
		className={`relative w-[38px] h-[21px] rounded-full flex-shrink-0 transition-colors duration-200 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 ${
			checked ? 'bg-[#667eea]' : 'bg-gray-300 dark:bg-gray-600'
		}`}
	>
		<span
			className={`absolute top-[3px] left-[3px] w-[15px] h-[15px] rounded-full bg-white shadow transition-transform duration-200 ${
				checked ? 'translate-x-[17px]' : ''
			}`}
		/>
	</button>
);

// ─── Day row ────────────────────────────────────────────────────

const DayRow = ({
	day,
	disabled,
	onChange,
}: {
	day: BusinessHoursDay;
	disabled: boolean;
	onChange: (next: BusinessHoursDay) => void;
}) => {
	const isOpen = !day.closed;

	const timeInputClasses =
		'w-[120px] px-2.5 py-1.5 rounded-lg border-[1.5px] border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white outline-none transition-colors focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 disabled:opacity-60 disabled:cursor-not-allowed';

	return (
		<div
			className={`grid grid-cols-1 sm:grid-cols-[120px_auto_1fr] items-start sm:items-center gap-3 sm:gap-4 px-4 sm:px-[18px] py-3.5 border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors ${
				isOpen
					? 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50'
					: 'bg-gray-50 dark:bg-gray-800/60'
			}`}
		>
			<div className="text-[13px] font-bold text-gray-900 dark:text-white">
				{DAY_LABELS[day.dayOfWeek]}
			</div>

			<div className="flex items-center gap-2">
				<ToggleSwitch
					checked={isOpen}
					disabled={disabled}
					ariaLabel={`${DAY_LABELS[day.dayOfWeek]} open`}
					onChange={checked =>
						// Turning a day off also drops its 24-hour flag, so re-opening
						// it starts from bounded hours rather than round-the-clock.
						onChange({ ...day, closed: !checked, open24: checked ? day.open24 : false })
					}
				/>
				<span className="text-xs font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">
					{day.closed ? 'Closed' : day.open24 ? 'Open 24h' : 'Open'}
				</span>
			</div>

			<div className="flex items-center gap-2.5 flex-wrap">
				{day.closed ? (
					<span className="text-xs font-semibold text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
						Closed
					</span>
				) : day.open24 ? (
					<span className="text-xs font-bold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 px-3.5 py-1 rounded-full">
						Open 24 hours
					</span>
				) : (
					<>
						<div className="flex items-center gap-2">
							<span className="text-[11px] font-medium text-gray-400 dark:text-gray-500 whitespace-nowrap">
								Opens
							</span>
							<input
								type="time"
								value={day.openTime}
								disabled={disabled}
								onChange={e => onChange({ ...day, openTime: e.target.value })}
								className={timeInputClasses}
								aria-label={`${DAY_LABELS[day.dayOfWeek]} opening time`}
							/>
						</div>
						<span className="text-sm text-gray-400 dark:text-gray-500">–</span>
						<div className="flex items-center gap-2">
							<span className="text-[11px] font-medium text-gray-400 dark:text-gray-500 whitespace-nowrap">
								Closes
							</span>
							<input
								type="time"
								value={day.closeTime}
								disabled={disabled}
								onChange={e => onChange({ ...day, closeTime: e.target.value })}
								className={timeInputClasses}
								aria-label={`${DAY_LABELS[day.dayOfWeek]} closing time`}
							/>
						</div>
						{isOvernight(day) && (
							<span className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-2.5 py-0.5 rounded-full whitespace-nowrap">
								Closes next day
							</span>
						)}
					</>
				)}

				{!day.closed && (
					<label
						className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border-[1.5px] text-[11px] font-semibold select-none transition-colors ${
							disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
						} ${
							day.open24
								? 'bg-indigo-50 dark:bg-indigo-900/30 border-[#667eea] text-indigo-700 dark:text-indigo-300'
								: 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-indigo-300 hover:text-indigo-600 dark:hover:text-indigo-300'
						}`}
					>
						<input
							type="checkbox"
							checked={day.open24}
							disabled={disabled}
							onChange={e => onChange({ ...day, open24: e.target.checked })}
							className="w-3 h-3 m-0 accent-[#667eea] cursor-pointer disabled:cursor-not-allowed"
						/>
						<span>24 hrs</span>
					</label>
				)}
			</div>
		</div>
	);
};

// ─── Affected bookings ──────────────────────────────────────────

/** Longest list shown before the panel collapses the rest behind a toggle. */
const AFFECTED_PREVIEW_COUNT = 4;

/** 45 → "45 min", 551 → "9h 11m". */
const formatDuration = (minutes?: number | null): string | null => {
	if (!minutes || minutes <= 0) return null;
	if (minutes < 60) return `${minutes} min`;
	const hours = Math.floor(minutes / 60);
	const rest = minutes % 60;
	return rest ? `${hours}h ${rest}m` : `${hours}h`;
};

/** "NO_SHOW" → "No show". The status enum is open-ended, so nothing is mapped by hand. */
const toStatusLabel = (status?: string | null): string | null => {
	const text = status?.replace(/_/g, ' ').trim().toLowerCase();
	return text ? text.charAt(0).toUpperCase() + text.slice(1) : null;
};

/** The server sends a ready-to-show label; the raw timestamp is only a fallback. */
const toWhenLabel = (booking: AffectedBooking): string => {
	if (booking.whenLabel?.trim()) return booking.whenLabel;
	const parsed = new Date(booking.appointmentDateTime);
	return Number.isNaN(parsed.getTime())
		? 'Date unavailable'
		: parsed.toLocaleString(undefined, {
				month: 'short',
				day: 'numeric',
				hour: 'numeric',
				minute: '2-digit',
			});
};

/**
 * Shown after a save that leaves existing bookings outside the new opening
 * hours. Nothing here has been cancelled — the panel exists so the provider can
 * see exactly what is affected and go deal with it, so it stays put until
 * dismissed rather than vanishing the way a toast would.
 */
const AffectedBookingsPanel = ({
	warning,
	bookings,
	onReview,
	onDismiss,
}: {
	warning: string | null;
	bookings: AffectedBooking[];
	onReview: () => void;
	onDismiss: () => void;
}) => {
	const [expanded, setExpanded] = useState(false);
	const visible = expanded ? bookings : bookings.slice(0, AFFECTED_PREVIEW_COUNT);
	const hiddenCount = bookings.length - visible.length;

	return (
		<div className="rounded-xl border-2 border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 sm:p-5 mb-5">
			<div className="flex items-start gap-3">
				<ExclamationTriangleIcon className="w-6 h-6 flex-shrink-0 text-amber-600 dark:text-amber-400" />

				<div className="flex-1 min-w-0">
					<h3 className="text-[15px] font-bold text-amber-900 dark:text-amber-200">
						Bookings outside your new hours
					</h3>
					<p className="mt-1 text-[13px] leading-relaxed text-amber-800 dark:text-amber-300/90">
						{warning ||
							`${bookings.length} upcoming ${bookings.length === 1 ? 'booking' : 'bookings'} now fall outside this location's opening hours. Please review — none have been cancelled.`}
					</p>

					<ul className="mt-3.5 overflow-hidden rounded-lg border border-amber-200 dark:border-amber-800/70 bg-white/70 dark:bg-gray-800/40 divide-y divide-amber-100 dark:divide-amber-900/40">
						{visible.map(booking => {
							const status = toStatusLabel(booking.status);
							const details = [
								booking.serviceName || 'Appointment',
								formatDuration(booking.durationMinutes),
								booking.staffName || 'Unassigned',
							]
								.filter(Boolean)
								.join(' · ');

							return (
								<li key={booking.appointmentId} className="px-3.5 py-2.5">
									<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
										<span className="text-[13px] font-bold text-gray-900 dark:text-white">
											{toWhenLabel(booking)}
										</span>
										{status && (
											<span className="rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800 dark:text-amber-300">
												{status}
											</span>
										)}
									</div>
									<div className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
										{details}
									</div>
								</li>
							);
						})}
					</ul>

					{(hiddenCount > 0 || expanded) && (
						<button
							type="button"
							onClick={() => setExpanded(!expanded)}
							className="mt-2 text-xs font-semibold text-amber-800 dark:text-amber-300 underline underline-offset-2 cursor-pointer"
						>
							{expanded ? 'Show fewer' : `Show all ${bookings.length}`}
						</button>
					)}

					<div className="mt-4 flex flex-wrap gap-2.5">
						<Button variant="primary" size="sm" onClick={onReview}>
							Review in Appointments
						</Button>
						<Button variant="secondary" size="sm" onClick={onDismiss}>
							Dismiss
						</Button>
					</div>
				</div>

				<button
					type="button"
					onClick={onDismiss}
					aria-label="Dismiss booking warning"
					className="flex-shrink-0 rounded-lg p-1 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40 cursor-pointer"
				>
					<XMarkIcon className="w-4 h-4" />
				</button>
			</div>
		</div>
	);
};

// ─── Salon closures ─────────────────────────────────────────────

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const todayIso = (): string => {
	const now = new Date();
	return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
		now.getDate()
	).padStart(2, '0')}`;
};

interface ClosureModalProps {
	isOpen: boolean;
	closure: LocationClosure | null;
	locationId: number;
	onClose: () => void;
	onSaved: (
		closure: LocationClosure,
		warning: string | null,
		affected: TimeOffSaveAffectedBooking[]
	) => void;
}

const ClosureModal = ({ isOpen, closure, locationId, onClose, onSaved }: ClosureModalProps) => {
	const [name, setName] = useState('');
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (!isOpen) return;
		setName(closure?.name ?? '');
		setStartDate(closure?.startDate ?? '');
		setEndDate(closure?.endDate ?? closure?.startDate ?? '');
	}, [isOpen, closure]);

	const isValid = name.trim() && startDate && endDate && endDate >= startDate;

	const handleSubmit = async () => {
		if (!isValid) return;
		setIsSaving(true);
		try {
			const payload = { name: name.trim(), startDate, endDate };
			const result = closure
				? await timeOffApi.updateClosure(closure.id, payload)
				: await timeOffApi.createClosure(locationId, payload);
			onSaved(result.closure, result.warning, result.affectedBookings ?? []);
			onClose();
		} catch (err) {
			toast.error((err as ApiError).message || 'Could not save the closure');
		} finally {
			setIsSaving(false);
		}
	};

	const inputClass =
		'w-full px-3.5 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] transition-colors';

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={closure ? 'Edit Closure' : 'Add Salon Closure'}
			size="md"
		>
			<div className="space-y-4">
				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
						Name <span className="text-red-500">*</span>
					</label>
					<input
						type="text"
						value={name}
						onChange={e => setName(e.target.value)}
						maxLength={80}
						placeholder="Thanksgiving, Renovation, Owner away…"
						className={inputClass}
					/>
				</div>
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
							First closed day <span className="text-red-500">*</span>
						</label>
						<input
							type="date"
							value={startDate}
							onChange={e => {
								setStartDate(e.target.value);
								if (!endDate || endDate < e.target.value)
									setEndDate(e.target.value);
							}}
							className={inputClass}
						/>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
							Last closed day <span className="text-red-500">*</span>
						</label>
						<input
							type="date"
							min={startDate}
							value={endDate}
							onChange={e => setEndDate(e.target.value)}
							className={inputClass}
						/>
					</div>
				</div>
				<p className="text-xs text-gray-500 dark:text-gray-400">
					A closure shuts the whole location: no online slots, no staff can be booked, and
					staff don’t need to request these days off. Existing bookings are never
					cancelled — any on these dates come back as a warning.
				</p>
			</div>
			<div className="flex justify-end gap-3 mt-6">
				<Button variant="secondary" onClick={onClose} disabled={isSaving}>
					Cancel
				</Button>
				<Button
					variant="primary"
					onClick={handleSubmit}
					disabled={!isValid}
					isLoading={isSaving}
				>
					{closure ? 'Save Changes' : 'Add Closure'}
				</Button>
			</div>
		</Modal>
	);
};

const ClosuresCard = ({
	locationId,
	onAffected,
}: {
	locationId: number;
	onAffected: (warning: string, affected: TimeOffSaveAffectedBooking[]) => void;
}) => {
	const [closures, setClosures] = useState<LocationClosure[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [includePast, setIncludePast] = useState(false);
	const [editing, setEditing] = useState<{ open: boolean; closure: LocationClosure | null }>({
		open: false,
		closure: null,
	});
	const [removing, setRemoving] = useState<LocationClosure | null>(null);
	const [isRemoving, setIsRemoving] = useState(false);

	const load = useCallback(async () => {
		setIsLoading(true);
		try {
			setClosures(await timeOffApi.listClosures(locationId, includePast));
		} catch (err) {
			toast.error((err as ApiError).message || 'Could not load closures');
		} finally {
			setIsLoading(false);
		}
	}, [locationId, includePast]);

	useEffect(() => {
		load();
	}, [load]);

	const handleRemove = async () => {
		if (!removing) return;
		setIsRemoving(true);
		try {
			await timeOffApi.deleteClosure(removing.id);
			setClosures(prev => prev.filter(c => c.id !== removing.id));
			toast.success(`Closure removed — ${removing.dateLabel} is open again`);
			setRemoving(null);
		} catch (err) {
			toast.error((err as ApiError).message || 'Could not remove the closure');
		} finally {
			setIsRemoving(false);
		}
	};

	const today = todayIso();

	return (
		<div className="bg-white dark:bg-gray-800 rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 dark:border-gray-700 mt-5">
			<div className="flex flex-wrap items-start justify-between gap-3 mb-4">
				<div>
					<h2 className="text-lg font-semibold text-gray-900 dark:text-white">
						Salon Closures
					</h2>
					<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
						Days the whole location is shut — holidays, renovations. Overrides the
						weekly hours, staff availability and online booking.
					</p>
				</div>
				<div className="flex items-center gap-3">
					<label className="inline-flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 cursor-pointer">
						<input
							type="checkbox"
							checked={includePast}
							onChange={e => setIncludePast(e.target.checked)}
							className="accent-[#667eea]"
						/>
						Show past
					</label>
					<Button
						variant="primary"
						size="sm"
						onClick={() => setEditing({ open: true, closure: null })}
					>
						+ Add Closure
					</Button>
				</div>
			</div>

			{isLoading ? (
				<div className="flex items-center justify-center py-8">
					<Spinner size="md" />
				</div>
			) : closures.length === 0 ? (
				<p className="text-sm text-gray-500 dark:text-gray-400 italic">
					No closures scheduled. The weekly hours above apply every week.
				</p>
			) : (
				<ul className="space-y-2">
					{closures.map(closure => {
						const past = closure.endDate < today;
						return (
							<li
								key={closure.id}
								className={`flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 ${
									past ? 'opacity-60' : ''
								}`}
							>
								<div className="w-9 h-9 rounded-lg bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-300 flex items-center justify-center flex-shrink-0">
									<LockClosedIcon className="w-5 h-5" />
								</div>
								<div className="min-w-0 flex-1">
									<p className="text-sm font-bold text-gray-900 dark:text-white truncate">
										{closure.name}
									</p>
									<p className="text-xs text-gray-500 dark:text-gray-400">
										{closure.dateLabel} · {plural(closure.dayCount, 'day')}
										{past ? ' · past' : ''}
									</p>
								</div>
								{!past && (
									<div className="flex items-center gap-1">
										<button
											type="button"
											onClick={() => setEditing({ open: true, closure })}
											title="Edit"
											aria-label="Edit closure"
											className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 cursor-pointer"
										>
											<PencilIcon className="w-4 h-4" />
										</button>
										<button
											type="button"
											onClick={() => setRemoving(closure)}
											title="Remove"
											aria-label="Remove closure"
											className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 cursor-pointer"
										>
											<TrashIcon className="w-4 h-4" />
										</button>
									</div>
								)}
							</li>
						);
					})}
				</ul>
			)}

			<ClosureModal
				isOpen={editing.open}
				closure={editing.closure}
				locationId={locationId}
				onClose={() => setEditing({ open: false, closure: null })}
				onSaved={(saved, warning, affected) => {
					setClosures(prev =>
						[...prev.filter(c => c.id !== saved.id), saved].sort((a, b) =>
							a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0
						)
					);
					toast.success(editing.closure ? 'Closure updated' : `Closed for ${saved.name}`);
					if (warning) onAffected(warning, affected);
				}}
			/>

			<ConfirmDialog
				isOpen={removing !== null}
				onClose={() => (isRemoving ? undefined : setRemoving(null))}
				onConfirm={handleRemove}
				isLoading={isRemoving}
				title="Remove this closure?"
				confirmLabel="Remove Closure"
				message={
					removing
						? `${removing.name} (${removing.dateLabel}) will be open as usual again.`
						: ''
				}
			/>
		</div>
	);
};

// ─── Page ───────────────────────────────────────────────────────

const BusinessHours = () => {
	const navigate = useNavigate();
	const { selectedLocationId, getSelectedLocation } = useProviderProfileStore();
	const selectedLocation = getSelectedLocation();

	const [days, setDays] = useState<BusinessHoursDay[]>([]);
	// Last state persisted on the server — powers both "Reset" and the dirty check.
	const [savedDays, setSavedDays] = useState<BusinessHoursDay[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// Bookings the last save pushed outside the open window. Only the save
	// response carries these, so a reload legitimately clears the panel.
	const [affected, setAffected] = useState<{
		warning: string | null;
		bookings: AffectedBooking[];
	} | null>(null);
	// Bookings sitting on dates a closure just shut. Same panel, same rule: never cancelled.
	const [closureAffected, setClosureAffected] = useState<{
		warning: string;
		bookings: AffectedBooking[];
	} | null>(null);

	const fetchHours = useCallback(async () => {
		if (!selectedLocationId) return;
		setIsLoading(true);
		setError(null);
		setAffected(null);
		try {
			const response = await businessHoursApi.get(selectedLocationId);
			const week = normalizeWeek(response);
			setDays(week);
			setSavedDays(week);
		} catch (err) {
			const apiError = err as ApiError;
			setError(apiError.message || 'Failed to load business hours');
		} finally {
			setIsLoading(false);
		}
	}, [selectedLocationId]);

	useEffect(() => {
		fetchHours();
	}, [fetchHours]);

	const isDirty = useMemo(
		() => JSON.stringify(days) !== JSON.stringify(savedDays),
		[days, savedDays]
	);

	const alwaysOpen = isAlwaysOpen(days);
	const openDays = days.filter(d => !d.closed);
	const allDayCount = openDays.filter(d => d.open24).length;

	const summary = useMemo(() => {
		if (days.length === 0) return null;
		if (alwaysOpen) return 'Open 24/7 — customers can book an appointment at any time.';
		if (openDays.length === 0)
			return 'This location is marked closed every day — customers cannot book online.';
		const dayPart = `Open ${openDays.length} day${openDays.length === 1 ? '' : 's'} a week`;
		const roundClock = allDayCount > 0 ? `, ${allDayCount} of them around the clock` : '';
		return `${dayPart}${roundClock}. These hours control when customers can book appointments online.`;
	}, [days.length, alwaysOpen, openDays.length, allDayCount]);

	const updateDay = (index: number, next: BusinessHoursDay) => {
		setDays(prev => prev.map((d, i) => (i === index ? next : d)));
	};

	const handleApplyMondayToAll = () => {
		const monday = days.find(d => d.dayOfWeek === 'MONDAY');
		if (!monday) return;
		setDays(prev =>
			prev.map(d => ({
				...d,
				closed: monday.closed,
				open24: monday.open24,
				openTime: monday.openTime,
				closeTime: monday.closeTime,
			}))
		);
		toast.success('Monday hours applied to every day');
	};

	const handleToggleAlwaysOpen = () => {
		if (alwaysOpen) {
			// Second tap undoes 24/7 and returns the week to bounded hours.
			setDays(prev => prev.map(d => ({ ...d, open24: false })));
			toast('24/7 turned off — back to set hours');
			return;
		}
		setDays(prev => prev.map(d => ({ ...d, closed: false, open24: true })));
		toast.success('Location set to open 24/7');
	};

	const handleReset = () => {
		setDays(savedDays);
		toast('Changes discarded');
	};

	const handleSave = async () => {
		if (!selectedLocationId) return;

		// A bounded day needs both times, and they cannot be identical — an
		// empty window would leave the booking page with no slots to offer.
		const incomplete = days.find(d => !d.closed && !d.open24 && (!d.openTime || !d.closeTime));
		if (incomplete) {
			toast.error(
				`Set both opening and closing times for ${DAY_LABELS[incomplete.dayOfWeek]}`
			);
			return;
		}
		const identical = days.find(d => !d.closed && !d.open24 && d.openTime === d.closeTime);
		if (identical) {
			toast.error(
				`${DAY_LABELS[identical.dayOfWeek]}: opening and closing time cannot match — use "24 hrs" for a round-the-clock day`
			);
			return;
		}

		setIsSaving(true);
		setAffected(null);
		try {
			const payload: BusinessHoursDay[] = days.map(d => ({
				dayOfWeek: d.dayOfWeek,
				closed: d.closed,
				open24: d.open24,
				openTime: d.closed || d.open24 ? '' : d.openTime,
				closeTime: d.closed || d.open24 ? '' : d.closeTime,
			}));
			const response = await businessHoursApi.update(selectedLocationId, payload);
			// Trust the server's echo so the grid matches what the booking page will use.
			const week = normalizeWeek(response);
			setDays(week);
			setSavedDays(week);
			toast.success(
				isAlwaysOpen(week) ? 'Saved — this location is open 24/7' : 'Business hours saved'
			);

			// Saving never cancels anything, so any booking left outside the new
			// window is surfaced for the provider to sort out themselves.
			const bookings = Array.isArray(response?.affectedBookings)
				? response.affectedBookings
				: [];
			if (bookings.length > 0) {
				setAffected({ warning: response?.warning || null, bookings });
			}
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(
				apiError.getFieldErrorMessages?.() ||
					apiError.message ||
					'Failed to save business hours'
			);
		} finally {
			setIsSaving(false);
		}
	};

	// ─── Render states ─────────────────────────────────────────

	if (!selectedLocationId && !isLoading) {
		return (
			<AppLayout>
				<div className="flex flex-col items-center justify-center py-20 gap-2">
					<ClockIcon className="w-12 h-12 text-gray-300 dark:text-gray-600" />
					<p className="text-gray-500 dark:text-gray-400">
						Select a location to set its business hours.
					</p>
				</div>
			</AppLayout>
		);
	}

	if (isLoading) {
		return (
			<AppLayout>
				<div className="flex items-center justify-center py-20">
					<Spinner size="lg" />
				</div>
			</AppLayout>
		);
	}

	if (error) {
		return (
			<AppLayout>
				<div className="flex flex-col items-center justify-center py-20 gap-4">
					<p className="text-red-500 dark:text-red-400">{error}</p>
					<button
						onClick={fetchHours}
						className="px-4 py-2 rounded-lg font-semibold text-sm text-white cursor-pointer"
						style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
					>
						Retry
					</button>
				</div>
			</AppLayout>
		);
	}

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="flex items-center gap-4 mb-6">
				<div
					className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
					style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
				>
					<ClockIcon className="w-6 h-6 text-white" />
				</div>
				<div>
					<h1 className="text-2xl font-bold text-gray-900 dark:text-white">
						Business Hours
					</h1>
					<p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
						Set the days and times
						{selectedLocation?.locationName
							? ` ${selectedLocation.locationName}`
							: ''}{' '}
						is open to customers
					</p>
				</div>
			</div>

			{/* Bookings the new hours pushed outside the open window */}
			{affected && (
				<AffectedBookingsPanel
					warning={affected.warning}
					bookings={affected.bookings}
					onReview={() => navigate('/providers/appointments')}
					onDismiss={() => setAffected(null)}
				/>
			)}
			{closureAffected && (
				<AffectedBookingsPanel
					warning={closureAffected.warning}
					bookings={closureAffected.bookings}
					onReview={() => navigate('/providers/appointments')}
					onDismiss={() => setClosureAffected(null)}
				/>
			)}

			{/* Summary bar */}
			{summary && (
				<div className="flex items-center gap-2.5 rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20 px-4 py-3 mb-5">
					<InformationCircleIcon className="w-5 h-5 flex-shrink-0 text-violet-600 dark:text-violet-300" />
					<span className="text-[13px] font-medium text-violet-900 dark:text-violet-200">
						{summary}
					</span>
				</div>
			)}

			{/* Weekly schedule */}
			<div className="bg-white dark:bg-gray-800 rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 dark:border-gray-700">
				<div className="flex flex-wrap items-start justify-between gap-3 mb-5">
					<div>
						<h2 className="text-lg font-semibold text-gray-900 dark:text-white">
							Weekly Schedule
						</h2>
						<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
							Set your opening and closing times for each day of the week
						</p>
					</div>
					<div className="flex items-center gap-3.5 flex-wrap">
						<button
							type="button"
							onClick={handleToggleAlwaysOpen}
							disabled={isSaving}
							className="text-xs font-semibold text-[#667eea] hover:text-indigo-700 underline underline-offset-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{alwaysOpen ? 'Turn off 24/7' : 'Open 24/7'}
						</button>
						<button
							type="button"
							onClick={handleApplyMondayToAll}
							disabled={isSaving}
							className="text-xs font-semibold text-[#667eea] hover:text-indigo-700 underline underline-offset-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
						>
							Apply Mon hours to all
						</button>
					</div>
				</div>

				<div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
					{days.map((day, index) => (
						<DayRow
							key={day.dayOfWeek}
							day={day}
							disabled={isSaving}
							onChange={next => updateDay(index, next)}
						/>
					))}
				</div>

				<div className="flex flex-wrap items-center justify-end gap-2.5 mt-5">
					{isDirty && !isSaving && (
						<span className="mr-auto text-xs font-semibold text-amber-600 dark:text-amber-400">
							You have unsaved changes
						</span>
					)}
					<Button
						variant="secondary"
						size="sm"
						onClick={handleReset}
						disabled={!isDirty || isSaving}
					>
						Reset
					</Button>
					<Button
						variant="primary"
						size="sm"
						onClick={handleSave}
						disabled={!isDirty}
						isLoading={isSaving}
					>
						{isSaving ? 'Saving…' : 'Save Hours'}
					</Button>
				</div>
			</div>

			{selectedLocationId && (
				<ClosuresCard
					locationId={selectedLocationId}
					onAffected={(warning, bookings) =>
						setClosureAffected({
							warning,
							bookings: bookings.map(b => ({
								appointmentId: b.appointmentId,
								appointmentDateTime: b.appointmentDateTime,
								whenLabel: b.whenLabel ?? '',
								serviceName: b.serviceName,
								durationMinutes: b.durationMinutes,
								locationId: b.locationId ?? 0,
								locationName: b.locationName,
								staffId: b.staffId,
								staffName: b.staffName,
								status: b.status,
							})),
						})
					}
				/>
			)}
		</AppLayout>
	);
};

export default BusinessHours;
